import React from 'react';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import { FormControl, FormHelperText, Grid, LinearProgress, MenuItem, Select, Box, Typography, Paper, IconButton, Button } from '@mui/material';
import { Form, Formik } from 'formik';
import { FMTextField } from '../Elements/input';
import useAuth from "../../context/AuthProvider";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import VpnKeyOutlinedIcon from '@mui/icons-material/VpnKeyOutlined';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import Swal from 'sweetalert2';
import { StateLoginsModel, StateLoginsModel_VS } from '../../models/StateLoginsModel';
import { apiClient } from '../../helper/api';

interface Iprops {
    item: StateLoginsModel;
    handleClose: () => void;
    Open: boolean;
    initValues: StateLoginsModel;
    states: { stateId: string, stateName: string }[];
    partners: { partnersId: number, customerName: string }[];
}

export const AddEditStateLogins: React.FC<Iprops> = ({ 
    handleClose, 
    Open, 
    item,
    initValues, 
    states, 
    partners 
}) => {
    const initialValues: StateLoginsModel = initValues ? item : initValues;
    const { user } = useAuth();
    const token = user?.token;

    const handleFormSubmit = async (data: any, { setSubmitting }: any) => {
        try {
            const res = await apiClient('/StateLogins/SaveOrUpdate', {
                method: 'POST',
                headers: new Headers({
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                }),
                body: JSON.stringify(data)
            });
            if (res?.status === true) {
                Swal.fire({
                    title: 'Success',
                    text: res.message || 'Data Successfully saved',
                    icon: "success",
                    confirmButtonText: "OK",
                });
                handleClose();
            }else {
                Swal.fire({
                    title: "Error!",
                    text: res?.message || "Something went wrong while saving.",
                    icon: "warning",
                    confirmButtonText: "OK",
                });
            }
        } catch (error) {
            console.error('Error saving state login:', error);
            Swal.fire({
                title: "Error!",
                text: "A network or server error occurred.",
                icon: "error",
                confirmButtonText: "OK",
            });
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog
            open={Open}
            onClose={handleClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    width: '90%',
                    maxWidth: 900,
                    borderRadius: 2,
                    boxShadow: '0 24px 48px rgba(0,0,0,0.12)',
                },
            }}
            sx={{ '& .MuiDialog-paper': { overflow: 'hidden' } }}
        >
            <Formik
                initialValues={initialValues}
                enableReinitialize={true}
                validateOnChange={true}
                validationSchema={StateLoginsModel_VS}
                onSubmit={handleFormSubmit}
            >
                {({ values, isSubmitting, setFieldValue, errors }) => (<>
                    
                        {isSubmitting && <LinearProgress sx={{ flexShrink: 0 }} />}
                        <DialogTitle
                            sx={{
                                position: 'sticky',
                                top: 0,
                                zIndex: 1,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                gap: 1,
                                py: 2,
                                px: 3,
                                background: 'linear-gradient(135deg, #0d4a63 0%, #15607a 100%)',
                                color: '#fff',
                                boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                            }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                <VpnKeyOutlinedIcon sx={{ fontSize: 28, opacity: 0.95 }} />
                                <Typography component="span" variant="h6" sx={{ fontWeight: 600, letterSpacing: '0.02em' }}>
                                    State Login
                                </Typography>
                            </Box>
                            <IconButton
                                onClick={handleClose}
                                sx={{ color: 'rgba(255,255,255,0.9)', '&:hover': { color: '#fff', backgroundColor: 'rgba(255,255,255,0.12)' } }}
                                aria-label="Close dialog"
                            >
                                <CloseOutlinedIcon />
                            </IconButton>
                        </DialogTitle>
                        <Form className='main-form' noValidate>
                        <DialogContent sx={{ width: '100%', minHeight: '53.5vh', overflow: 'scroll', px: { xs: 2, sm: 3 }, py: 3 }}>
                            <Typography variant="overline" sx={{ color: 'text.secondary', fontWeight: 600, letterSpacing: '0.08em', display: 'block', mb: 1 }}>
                                Login details
                            </Typography>
                            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, borderColor: 'divider', backgroundColor: 'grey.50', mb: 2 }}>
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={6}>
                                    <FormControl fullWidth>
                                        <FormHelperText className='formtexthelper-heading'>
                                            Licensee
                                        </FormHelperText>
                                        <FMTextField 
                                            name="licensee" 
                                            autoComplete='off' 
                                            margin="dense" 
                                            label={null} 
                                            type="text" 
                                            fullWidth 
                                            variant="outlined" 
                                            sx={{
                                                '& .MuiOutlinedInput-notchedOutline': { border: '2px solid #e2e8f0 !important', borderRadius: '8px !important' },
                                                '& .MuiInputBase-input': { padding: '5px 14px !important' },
                                                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(0, 0, 0, 0.87) !important' },
                                                '& .Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#1976d2 !important' }
                                            }}
                                        />
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <FormControl error={!!errors.statename} fullWidth>
                                        <FormHelperText className='formtexthelper-heading'>
                                            State
                                        </FormHelperText>
                                        <Select
                                            value={values.statename}
                                            onChange={(e) => {
                                                const val = e.target.value;
                                                const sel = states.find(v => v.stateName === val);
                                                setFieldValue("statename", val);
                                                setFieldValue("stateId", sel?.stateId);
                                            }}
                                            name="statename"
                                            className="border-round"
                                            IconComponent={ExpandMoreIcon}
                                            displayEmpty
                                            sx={{
                                                width: '100%',
                                                '& .MuiSelect-select': { padding: '14.5px 16px' },
                                                '& .MuiOutlinedInput-notchedOutline': { border: '2px solid #e2e8f0 !important', borderRadius: '8px' },
                                                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(0, 0, 0, 0.87) !important' },
                                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#1976d2 !important' }
                                            }}
                                        >
                                            <MenuItem key={0} value={''}>
                                                Select State
                                            </MenuItem>
                                            {states.map((state, key) => (
                                                <MenuItem key={key} value={state.stateName}>
                                                    {state.stateName}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                        <FormHelperText error>{errors.statename}</FormHelperText>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <FormControl fullWidth>
                                        <FormHelperText className='formtexthelper-heading'>
                                            Website
                                        </FormHelperText>
                                        <FMTextField 
                                            name="website" 
                                            autoComplete='off' 
                                            margin="dense" 
                                            label={null} 
                                            type="text" 
                                            fullWidth 
                                            variant="outlined" 
                                            sx={{
                                                '& .MuiOutlinedInput-notchedOutline': { border: '2px solid #e2e8f0 !important', borderRadius: '8px !important' },
                                                '& .MuiInputBase-input': { padding: '5px 14px !important' },
                                                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(0, 0, 0, 0.87) !important' },
                                                '& .Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#1976d2 !important' }
                                            }}
                                        />
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <FormControl error={!!errors.client} fullWidth>
                                        <FormHelperText className='formtexthelper-heading'>
                                            Client
                                        </FormHelperText>
                                        <Select
                                            value={values.client}
                                            onChange={(e) => {
                                                const val = e.target.value;
                                                const sel = partners.find(v => v.customerName === val);
                                                setFieldValue("client", val);
                                                setFieldValue("partnersId", sel?.partnersId);
                                            }}
                                            name="client"
                                            className="border-round"
                                            IconComponent={ExpandMoreIcon}
                                            displayEmpty
                                            sx={{
                                                width: '100%',
                                                '& .MuiSelect-select': { padding: '14.5px 16px' },
                                                '& .MuiOutlinedInput-notchedOutline': { border: '2px solid #e2e8f0 !important', borderRadius: '8px' },
                                                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(0, 0, 0, 0.87) !important' },
                                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#1976d2 !important' }
                                            }}
                                        >
                                            <MenuItem key={0} value={''}>
                                                Select Client
                                            </MenuItem>
                                            {partners.map((client, key) => (
                                                <MenuItem key={key} value={client.customerName}>
                                                    {client.customerName}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                        <FormHelperText error>{errors.client}</FormHelperText>
                                    </FormControl>
                                </Grid>
                            </Grid>
                        </Paper>
                        <Typography variant="overline" sx={{ color: 'text.secondary', fontWeight: 600, letterSpacing: '0.08em', display: 'block', mb: 1 }}>
                            Security Credentials
                        </Typography>
                        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, borderColor: 'divider', backgroundColor: 'grey.50', mb: 2 }}>
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={12}>
                                    <FormControl fullWidth>
                                        <FormHelperText className='formtexthelper-heading'>
                                            Username
                                        </FormHelperText>
                                        <FMTextField 
                                            name="username" 
                                            autoComplete='off' 
                                            margin="dense" 
                                            label={null} 
                                            type="text" 
                                            fullWidth 
                                            variant="outlined" 
                                            sx={{
                                                '& .MuiOutlinedInput-notchedOutline': { border: '2px solid #e2e8f0 !important', borderRadius: '8px !important' },
                                                '& .MuiInputBase-input': { padding: '5px 14px !important' },
                                                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(0, 0, 0, 0.87) !important' },
                                                '& .Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#1976d2 !important' }
                                            }}
                                        />
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <FormControl fullWidth>
                                        <FormHelperText className='formtexthelper-heading'>
                                            Password1
                                        </FormHelperText>
                                        <FMTextField 
                                            name="password1" 
                                            autoComplete='off' 
                                            margin="dense" 
                                            label={null} 
                                            type="text" 
                                            fullWidth 
                                            variant="outlined" 
                                            sx={{
                                                '& .MuiOutlinedInput-notchedOutline': { border: '2px solid #e2e8f0 !important', borderRadius: '8px !important' },
                                                '& .MuiInputBase-input': { padding: '5px 14px !important' },
                                                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(0, 0, 0, 0.87) !important' },
                                                '& .Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#1976d2 !important' }
                                            }}
                                        />
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <FormControl fullWidth>
                                        <FormHelperText className='formtexthelper-heading'>
                                            Password2
                                        </FormHelperText>
                                        <FMTextField 
                                            name="password2" 
                                            autoComplete='off' 
                                            margin="dense" 
                                            label={null} 
                                            type="text" 
                                            fullWidth 
                                            variant="outlined" 
                                            sx={{
                                                '& .MuiOutlinedInput-notchedOutline': { border: '2px solid #e2e8f0 !important', borderRadius: '8px !important' },
                                                '& .MuiInputBase-input': { padding: '5px 14px !important' },
                                                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(0, 0, 0, 0.87) !important' },
                                                '& .Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#1976d2 !important' }
                                            }}
                                        />
                                    </FormControl>
                                </Grid>
                            </Grid>
                        </Paper>
                        <Typography variant="overline" sx={{ color: 'text.secondary', fontWeight: 600, letterSpacing: '0.08em', display: 'block', mb: 1 }}>
                            Account Information & Notes
                        </Typography>
                        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, borderColor: 'divider', backgroundColor: 'grey.50', mb: 2 }}>
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={6}>
                                    <FormControl fullWidth>
                                        <FormHelperText className='formtexthelper-heading'>
                                            Notes
                                        </FormHelperText>
                                        <FMTextField 
                                            name="notes" 
                                            autoComplete='off' 
                                            margin="dense" 
                                            label={null} 
                                            type="text" 
                                            fullWidth 
                                            variant="outlined" 
                                            sx={{
                                                '& .MuiOutlinedInput-notchedOutline': { border: '2px solid #e2e8f0 !important', borderRadius: '8px !important' },
                                                '& .MuiInputBase-input': { padding: '5px 14px !important' },
                                                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(0, 0, 0, 0.87) !important' },
                                                '& .Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#1976d2 !important' }
                                            }}
                                        />
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <FormControl fullWidth>
                                        <FormHelperText className='formtexthelper-heading'>
                                            Account ID
                                        </FormHelperText>
                                            <FMTextField 
                                                name="accountID" 
                                                autoComplete='off' 
                                                margin="dense" 
                                                label={null} 
                                                type="text" 
                                                fullWidth 
                                                variant="outlined" 
                                                sx={{
                                                    '& .MuiOutlinedInput-notchedOutline': { border: '2px solid #e2e8f0 !important', borderRadius: '8px !important' },
                                                    '& .MuiInputBase-input': { padding: '5px 14px !important' },
                                                    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(0, 0, 0, 0.87) !important' },
                                                    '& .Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#1976d2 !important' }
                                                }}
                                        />
                                    </FormControl>
                                </Grid>
                            </Grid>
                            </Paper>
                        </DialogContent>
                        <DialogActions
                            sx={{
                                px: 3,
                                py: 2,
                                gap: 1.5,
                                borderTop: 1,
                                borderColor: 'divider',
                                backgroundColor: 'grey.50',
                                flexWrap: 'wrap',
                            }}
                        >
                            <Button
                                variant="outlined"
                                size="medium"
                                onClick={handleClose}
                                startIcon={<CloseOutlinedIcon />}
                                sx={{
                                    textTransform: 'none',
                                    fontWeight: 600,
                                    borderColor: '#e2e8f0',
                                    color: '#475569',
                                    '&:hover': {
                                        borderColor: '#2099d2',
                                        backgroundColor: '#2099d214',
                                        color: '#2099d2',
                                    },
                                }}
                            >
                                Cancel
                            </Button>
                            <Button
                                variant="contained"
                                size="medium"
                                type="submit"
                                disabled={isSubmitting}
                                sx={{
                                    textTransform: 'none',
                                    fontWeight: 600,
                                    backgroundColor: '#2099d2',
                                    '&:hover': { backgroundColor: '#176990' },
                                    px: 2.5,
                                }}
                            >
                                Submit
                            </Button>
                        </DialogActions>
                    </Form>
                </>)}
            </Formik>
        </Dialog>
    );
};
